import React, { useEffect, Suspense, useState, useRef } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { I18nextProvider } from 'react-i18next';
import { NotificationProvider } from './core/notifications/NotificationProvider';
import { TourProvider } from '@/components/tour/TourProvider';
import { useSettingsStore } from '@core/store';
import { AppUnsavedChangesDialog, LoadingSpinner, StatusDialog } from '@/components';
import { LoadingOverlay } from '@/components/shared/LoadingOverlay';
import { Header } from './layouts/Header';
import { Sidebar } from './layouts/Sidebar';
import { createAppTheme } from '@core/theme';
import i18n from './core/i18n';
import { AuthProvider, useAuth } from '@core/auth/AuthProvider';
import { routes } from '@/route/Routes';
import { MobileHeaderPanelProvider } from '@layouts/MobileHeaderPanel.tsx';
import { useAppInitialization } from '@core/hooks/useAppInitialization.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { AnalyticsProvider, AnalyticsRouteTracker } from '@core/analytics';
import { PageActionProvider } from '@core/contexts/PageActionContext';

// Lazy load pages
const LoginPage = React.lazy(() => import('./features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));

// Protected Route Wrapper Component
const ProtectedRouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return <LoadingSpinner color="white" />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// Main Layout Component
const MainLayout: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 480);

    // Page action overlay state
    const [isPageLoading, setIsPageLoading] = useState(false);
    const inFlightRef = useRef(0);

    const { darkMode, isMobile, setIsMobile, hasMobilePanel, isShowFilterBar } = useSettingsStore();
    const prevIsMobileRef = useRef(isMobile);

    const handleMenuClick = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleSidebarClose = () => {
        setSidebarOpen(false);
    };

    // Page action overlay — subscribes to PAGE_LOADING/LOADED/ERROR from any registered action.
    // inFlightRef counts concurrent operations so the overlay only hides when all finish.
    useEffect(() => {
        const show = () => {
            inFlightRef.current += 1;
            setIsPageLoading(true);
        };
        const hide = () => {
            inFlightRef.current = Math.max(0, inFlightRef.current - 1);
            if (inFlightRef.current === 0) setIsPageLoading(false);
        };
        const unsubLoading = eventBus.on(EventTypes.PAGE_LOADING, show);
        const unsubLoaded = eventBus.on(EventTypes.PAGE_LOADED, hide);
        const unsubError = eventBus.on(EventTypes.PAGE_ERROR, hide);
        return () => {
            unsubLoading();
            unsubLoaded();
            unsubError();
        };
    }, []);

    // Apply dark theme to body
    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    // Handle resize for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            const isNowMobile = window.innerWidth <= 480;
            setIsMobile(isNowMobile);

            const prevIsMobile = prevIsMobileRef.current;

            // Auto-close sidebar when switching from desktop to mobile
            if (!prevIsMobile && isNowMobile && sidebarOpen) {
                setSidebarOpen(false);
            }
            // Auto-open sidebar when switching from mobile to desktop
            else if (prevIsMobile && !isNowMobile) {
                setSidebarOpen(true);
            }

            prevIsMobileRef.current = isNowMobile;
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [sidebarOpen]);

    return (
        <MobileHeaderPanelProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                {/* Header */}
                <Header onMenuClick={handleMenuClick} />

                {/* Content area */}
                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        paddingTop: 'var(--header-height)',
                        '@media (max-width: 780px)': {
                            paddingTop: hasMobilePanel ? (isShowFilterBar ? '132px' : '86px') : '50px'
                        }
                    }}
                >
                    {/* Sidebar */}
                    <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />

                    {/* Main content area */}
                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            marginLeft: sidebarOpen && !isMobile ? 'var(--sidebar-width)' : '0px',
                            width: sidebarOpen && !isMobile ? 'calc(100% - var(--sidebar-width))' : '100%',
                            transition: 'margin-left 0.3s ease, width 0.3s ease',
                            height: 'calc(100vh - var(--header-height))',
                            overflow: 'auto',
                            position: 'relative',
                            backgroundColor: darkMode ? '#121212' : '#F7F7F7',
                            '@media (max-width: 480px)': {
                                marginLeft: 0,
                                width: '100%',
                                height: 'calc(100vh - 50px)'
                            }
                        }}
                    >
                        {/* Global page action overlay */}
                        <LoadingOverlay open={isPageLoading} />

                        <Suspense fallback={<LoadingSpinner color="white" />}>
                            <Outlet />
                        </Suspense>
                    </Box>
                </Box>
            </Box>
        </MobileHeaderPanelProvider>
    );
};

// Root Layout Component - handles analytics tracking
const RootLayout: React.FC = () => {
    const { isAuthenticated, isLoading: authLoading, loginVersion } = useAuth();
    const { initializeApp, refreshAppData, isLoading: initLoading } = useAppInitialization();
    const navigate = useNavigate();

    // Handle app initialization when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            initializeApp().catch((error) => {
                console.error('Failed to initialize app:', error);
                // Show an error notification here
            });
        }
    }, [isAuthenticated, initializeApp, loginVersion]);

    useEffect(() => {
        const unsubscribe = eventBus.on<string>(EventTypes.ORGANISATION_CHANGED, (orgId) => {
            refreshAppData(orgId).catch((error) => {
                console.error('Failed to reload after organisation change:', error);
            });
            navigate('/myshops', { replace: true });
        });
        return unsubscribe;
    }, [refreshAppData]);

    // Show loading while auth or initialization is in progress
    if (authLoading || initLoading) {
        return <LoadingSpinner color="white" />;
    }

    return (
        <>
            {/* Analytics Route Tracker - automatically tracks page views */}
            <AnalyticsRouteTracker />
            <Outlet />
        </>
    );
};

// Create router configuration
const createAppRouter = () => {
    return createBrowserRouter([
        {
            path: '/',
            element: <RootLayout />,
            children: [
                // Login route (public)
                {
                    path: 'login',
                    element: (
                        <Suspense fallback={<LoadingSpinner color="white" />}>
                            <LoginPage />
                        </Suspense>
                    ),
                    handle: { name: 'Login' }
                },
                // Protected routes with main layout
                {
                    path: '/',
                    element: (
                        <ProtectedRouteWrapper>
                            <PageActionProvider>
                                <MainLayout />
                            </PageActionProvider>
                        </ProtectedRouteWrapper>
                    ),
                    children: routes // Your auto-generated routes from Routes.tsx
                }
            ]
        }
    ]);
};

// Main App Component
const App: React.FC = () => {
    const { darkMode, selectedLocale } = useSettingsStore();
    const [router] = useState(() => createAppRouter());

    // Create theme based on dark mode setting
    const theme = createAppTheme(darkMode ? 'dark' : 'light');

    // Set locale for i18n
    useEffect(() => {
        i18n.changeLanguage(selectedLocale.locale);
    }, [selectedLocale]);

    // Handle viewport height for mobile devices
    useEffect(() => {
        const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setVH();
        window.addEventListener('resize', setVH);
        window.addEventListener('orientationchange', setVH);

        return () => {
            window.removeEventListener('resize', setVH);
            window.removeEventListener('orientationchange', setVH);
        };
    }, []);

    return (
        <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AuthProvider>
                    <AnalyticsProvider>
                        <TourProvider>
                            <RouterProvider router={router} />
                            <NotificationProvider />
                            <StatusDialog />
                            <AppUnsavedChangesDialog />
                        </TourProvider>
                    </AnalyticsProvider>
                </AuthProvider>
            </ThemeProvider>
        </I18nextProvider>
    );
};

export default App;
